import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, CircularProgress, Checkbox,
  FormControlLabel, Accordion, AccordionSummary, AccordionDetails,
  Chip, Alert, Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import { useAlert } from "../functions/useAlert";
import {
  fetchOntologyClasses,
  fetchFactFindOntology,
  saveFactFindOntologySelection,
} from "../services/ontology.service";
import factfindSchema from "../meeting/factfind.json";

// Strip leading "has " prefix and capitalise first letter for display
const formatLabel = (label: string): string => {
  const stripped = label.replace(/^has\s+/i, "").replace(/^has([A-Z])/g, "$1");
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};
const collectFields = (fields: any[]): any[] => {
  const result: any[] = [];
  for (const f of fields) {
    if (f.subsection_name && Array.isArray(f.fields)) {
      result.push(...collectFields(f.fields));
    } else if (f.displayLabel) {
      result.push({ iri: f.graphLabel || f.displayLabel, label: f.displayLabel, range: f.type || "string" });
    }
  }
  return result;
};

const buildFallbackClasses = () =>
  ((factfindSchema as any).factfindTemplate?.categories || []).map((cat: any) => ({
    iri: cat.name,
    label: cat.name.split(" ").map((w: string) => w.charAt(0) + w.slice(1).toLowerCase()).join(" "),
    comment: "",
    properties: (cat.blocks || []).flatMap((b: any) => collectFields(b.fields || [])),
  }));

// ─── Types ────────────────────────────────────────────────────────────────────
interface OntologyProperty { iri: string; label: string; range: string; comment?: string; }
interface OntologyClass { iri: string; label: string; comment?: string; properties: OntologyProperty[]; }
interface SelectedClass { class_iri: string; properties: string[]; }

interface Props { selectedOrganization?: string; }

const FactFindOntologyPicker: React.FC<Props> = ({ selectedOrganization }) => {
  const [setAlert] = useAlert();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState<OntologyClass[]>([]);
  const [selected, setSelected] = useState<SelectedClass[]>([]);
  const [usingFallback, setUsingFallback] = useState(false);

  const orgId = selectedOrganization && selectedOrganization !== "All Organisation"
    ? selectedOrganization : null;

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [classesData, selectionData]: [any, any] = await Promise.all([
        fetchOntologyClasses(orgId),
        fetchFactFindOntology(orgId),
      ]);
      if (!classesData?.classes) throw new Error("No classes returned");
      setClasses(classesData.classes || []);
      setSelected(selectionData?.selected || []);
      setUsingFallback(false);
    } catch {
      const fallback = buildFallbackClasses();
      setClasses(fallback);
      setSelected(fallback.map((cls: OntologyClass) => ({
        class_iri: cls.iri,
        properties: cls.properties.map((p) => p.iri),
      })));
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isClassChecked = (iri: string) => selected.some((s) => s.class_iri === iri);
  const isPropChecked = (classIri: string, propIri: string) =>
    selected.find((s) => s.class_iri === classIri)?.properties.includes(propIri) ?? false;
  const isClassIndeterminate = (cls: OntologyClass) =>
    !isClassChecked(cls.iri) && cls.properties.some((p) => isPropChecked(cls.iri, p.iri));

  const toggleClass = (cls: OntologyClass) => {
    setSelected((prev) => {
      if (prev.find((s) => s.class_iri === cls.iri))
        return prev.filter((s) => s.class_iri !== cls.iri);
      return [...prev, { class_iri: cls.iri, properties: cls.properties.map((p) => p.iri) }];
    });
  };

  const toggleProp = (classIri: string, propIri: string) => {
    setSelected((prev) => {
      const existing = prev.find((s) => s.class_iri === classIri);
      if (!existing) return [...prev, { class_iri: classIri, properties: [propIri] }];
      const newProps = existing.properties.includes(propIri)
        ? existing.properties.filter((p) => p !== propIri)
        : [...existing.properties, propIri];
      if (newProps.length === 0) return prev.filter((s) => s.class_iri !== classIri);
      return prev.map((s) => s.class_iri === classIri ? { ...s, properties: newProps } : s);
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (usingFallback) { setAlert("Python API unavailable — cannot save.", "warning"); return; }
    setSaving(true);
    try {
      const body: any = { selected };
      if (orgId) body.org_id = orgId;
      const data: any = await saveFactFindOntologySelection(body);
      if (data?.selected) setSelected(data.selected);
      setAlert("Fact Find ontology saved successfully!", "success");
    } catch (err: any) {
      setAlert(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={2}>
        <CircularProgress size={28} />
        <Typography color="text.secondary">Loading ontology…</Typography>
      </Box>
    );
  }

  const selectedCount = selected.length;
  const totalProps = selected.reduce((acc, s) => acc + s.properties.length, 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e0e0e0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Fact Find Ontology</Typography>
          <Typography variant="body2" color="text.secondary">
            Select classes and properties to include.
            {orgId && <Chip label={`Org: ${orgId.slice(-6)}`} size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {selectedCount > 0 && (
            <Chip label={`${selectedCount} class${selectedCount !== 1 ? "es" : ""}, ${totalProps} props`} size="small" color="primary" variant="outlined" />
          )}
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving} sx={{ fontWeight: 700, borderRadius: 2 }}>
            {saving ? "Saving…" : "Save to Database"}
          </Button>
        </Stack>
      </Box>

      {usingFallback && (
        <Alert severity="info" sx={{ mx: 2, mt: 1, flexShrink: 0 }} action={<Button size="small" onClick={load}>Retry</Button>}>
          Python API unavailable — showing local schema.
        </Alert>
      )}

      {/* Class tree */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {classes.map((cls) => {
          const classChecked = isClassChecked(cls.iri);
          const indeterminate = isClassIndeterminate(cls);
          return (
            <Accordion key={cls.iri} disableGutters defaultExpanded={false}
              sx={{ mb: 1.5, border: "1px solid #e0e0e0", borderRadius: "10px !important", "&:before": { display: "none" }, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>

              {/* ── Class header ── */}
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: classChecked ? "#e3f0fb" : "#f5f5f5", borderRadius: "10px 10px 0 0", minHeight: 52 }}>
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  control={
                    <Checkbox checked={classChecked} indeterminate={indeterminate}
                      onChange={() => toggleClass(cls)} sx={{ color: "#1976d2" }} />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={700} fontSize="0.92rem">{formatLabel(cls.label)}</Typography>
                      {cls.comment && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 500, lineHeight: 1.3 }}>
                          {cls.comment}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Chip label={`${cls.properties.length} props`} size="small"
                  sx={{ ml: "auto", mr: 1, height: 20, fontSize: "0.65rem", alignSelf: "center", bgcolor: "#e0e0e0" }} />
              </AccordionSummary>

              {/* ── Properties ── */}
              <AccordionDetails sx={{ p: 0 }}>
                {cls.properties.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 3, py: 1.5, display: "block" }}>
                    No properties
                  </Typography>
                ) : (
                  cls.properties.map((prop, idx) => {
                    const propChecked = isPropChecked(cls.iri, prop.iri);
                    return (
                      <Box key={prop.iri}
                        sx={{
                          display: "flex", alignItems: "center", px: 2, py: 0.75,
                          borderTop: idx === 0 ? "1px solid #e0e0e0" : "none",
                          borderBottom: "1px solid #f0f0f0",
                          bgcolor: propChecked ? "#f0f7ff" : "white",
                          "&:hover": { bgcolor: propChecked ? "#e3f0fb" : "#fafafa" },
                          transition: "background 0.15s",
                        }}>
                        <Checkbox size="small" checked={propChecked}
                          onChange={() => toggleProp(cls.iri, prop.iri)}
                          sx={{ py: 0.5 }} />
                        <Box sx={{ flex: 1, ml: 0.5 }}>
                          <Typography variant="body2" fontWeight={propChecked ? 600 : 400} fontSize="0.83rem">
                            {formatLabel(prop.label)}
                          </Typography>
                          {prop.comment && (
                            <Typography variant="caption" color="text.secondary" fontSize="0.72rem">
                              {prop.comment}
                            </Typography>
                          )}
                        </Box>
                        <Chip label={prop.range} size="small"
                          sx={{ height: 18, fontSize: "0.62rem", bgcolor: "#f0f0f0", ml: 1, flexShrink: 0 }} />
                      </Box>
                    );
                  })
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default FactFindOntologyPicker;
